import { Injectable } from '@angular/core';
import { ProductService } from '@epicuro-next/data/product';
import { selectConfigurationValueFrom } from '@epicuro-next/platform/configuration';
import {
  selectProduct,
  upsertProducts,
} from '@epicuro-next/state/product-entities';
import { selectLookbookIdInRoute } from '@epicuro-next/state/shop-the-look-state';
import { isNonNullish, isNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { Product } from 'graphql-api/gql-api-interfaces';
import { combineLatest, EMPTY, of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';

import {
  ConfigStripStateConfigurationKeys,
  ConfigStripStateConfigurations,
  FEATURE_NAME,
} from '../../configurations';
import {
  cachedProductResolved,
  loadProduct,
  loadProductFailure,
  loadProductSuccess,
  loadSizeRuns,
} from '../actions/config-strip.actions';
import {
  loadCombinedCallPrices,
  selectTrueDemandFeatureEnabled,
} from '../selectors/config-strip-configuration.selectors';

@Injectable()
export class ProductEntitiesFromConfigStripEffects {
  private loadSizeRuns$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        ConfigStripStateConfigurationKeys.SizeRunsEnabled,
        ConfigStripStateConfigurations
      >(FEATURE_NAME, ConfigStripStateConfigurationKeys.SizeRunsEnabled, false),
    ),
  );

  public loadProductFromStore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadProduct),
      switchMap(({ productId, brandId, variantId }) =>
        combineLatest([
          this.store.pipe(select(selectProduct(productId))),
          this.store.pipe(select(selectProduct(variantId ?? productId))),
        ]).pipe(
          map(([cachedStyle, cachedVariant]) => cachedVariant || cachedStyle),
          filter(isNonNullish),
          takeUntil(
            this.actions$.pipe(
              ofType(loadProductSuccess),
              filter(
                ({
                  product: {
                    productId: loadedProductId,
                    brandId: loadedBrandId,
                  },
                }) =>
                  (loadedProductId === productId ||
                    loadedProductId === variantId) &&
                  loadedBrandId === brandId,
              ),
            ),
          ),
          catchError(() => EMPTY),
          map((product) => cachedProductResolved({ product })),
        ),
      ),
    ),
  );

  public loadProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadProduct),
      concatLatestFrom(() => [
        this.store.select(loadCombinedCallPrices),
        this.loadSizeRuns$,
        this.store.select(selectTrueDemandFeatureEnabled),
      ]),
      switchMap(
        ([
          { productId, brandId },
          loadPrices,
          shouldLoadSizeRuns,
          loadTrueDemand,
        ]) =>
          this.productService
            .loadProduct(productId, brandId, loadPrices, loadTrueDemand)
            .pipe(
              take(1),
              tap((product) => {
                if (!product) {
                  throw new Error('Cannot find product');
                }
              }),
              map((product: Product) => {
                if (shouldLoadSizeRuns) {
                  return loadSizeRuns({ product });
                }

                return loadProductSuccess({
                  product,
                });
              }),
              catchError((error: Error) =>
                of(loadProductFailure({ error: error.message })),
              ),
            ),
      ),
    ),
  );

  public updateProductEntitiesState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadProductSuccess),
      concatLatestFrom(() => this.store.pipe(select(selectLookbookIdInRoute))),
      filter(([, lookBookId]) => isNullish(lookBookId)),
      map(([{ product }]) =>
        upsertProducts({
          products: (product.children ?? []).concat(
            product as Product,
          ) as Product[],
        }),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private productService: ProductService,
    private store: Store,
  ) {}
}
